/**
 * PATH: packages/email-hub/src/feature/hooks/useMailboxContextMenu.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USE-MAILBOX-CONTEXT-MENU-001
 * PURPOSE: Own the right-click context-menu + per-row sync state for the
 *          ConnectedMailboxesPanel so the panel stays pure layout.
 * WHY: Keeps the menu/sync state machine (open coords, in-flight sync id,
 *      item assembly) in one testable place and the panel under the 300-line
 *      limit.
 * FLOW:
 * ┌───────────────────┐ ┌────────────────────┐ ┌──────────────────────┐
 * │ host callbacks     │ → │ syncOne / buildItems│ → │ {menu, openMenu,     │
 * │ (sync/toggle/...)  │   │ + open/close state │   │  closeMenu, syncId}  │
 * └───────────────────┘ └────────────────────┘ └──────────────────────┘
 * DEPENDENCIES:
 * - mailboxMenuItems: pure item builder
 * RELATED:
 * - ConnectedMailboxesPanel.tsx: sole consumer
 */
import { useCallback, useState } from 'react'
import {
  buildMailboxMenuItems,
  type MailboxMenuItem,
} from '../lib/mailboxMenuItems'
import type { EmailAccount } from './useEmails'

export type MailboxMenuState = { x: number; y: number; items: MailboxMenuItem[] }

/** Per-row inputs needed to assemble that mailbox's menu. */
export interface MailboxRowMenuContext {
  realAccountId: number | null
  isHidden: boolean
  key: string | null
}

export interface UseMailboxContextMenuInput {
  onSyncAccount: (accountId: number) => void | Promise<void>
  toggleMailbox: (key: string) => void
  onReconnect?: (acct: EmailAccount) => void
  onOpenSettings?: (tab: string) => void
  /** Quotech user id of the viewer — used as the fallback owner check for
   *  the privacy toggle when the server flag (can_manage_privacy) is absent. */
  currentUserId?: number | null
  /** Flip a mailbox between private (personal) and shared. When omitted the
   *  privacy action is hidden. */
  onSetPrivacy?: (accountId: number, makePrivate: boolean) => void | Promise<void>
  /** Open Clean up inbox dialog for a real mailbox account id. */
  onCleanUpInbox?: (accountId: number, mailboxLabel: string) => void
}

export function useMailboxContextMenu(input: UseMailboxContextMenuInput) {
  const {
    onSyncAccount,
    toggleMailbox,
    onReconnect,
    onOpenSettings,
    currentUserId,
    onSetPrivacy,
    onCleanUpInbox,
  } = input
  const [syncingId, setSyncingId] = useState<number | null>(null)
  const [menu, setMenu] = useState<MailboxMenuState | null>(null)

  const syncOne = useCallback(
    async (accountId: number) => {
      if (accountId <= 0) return
      setSyncingId(accountId)
      try {
        await onSyncAccount(accountId)
      } finally {
        setSyncingId(null)
      }
    },
    [onSyncAccount],
  )

  const buildItems = useCallback(
    (acct: EmailAccount, ctx: MailboxRowMenuContext): MailboxMenuItem[] => {
      const canSyncOne = ctx.realAccountId != null && ctx.realAccountId > 0
      const isPrivate = acct.account_type === 'personal'
      // Privacy is managed by the owner OR an org admin/owner and needs a
      // real account id to PATCH. Prefer the server-computed flag; fall
      // back to the owner check for older backends that don't send it.
      const ownerId = acct.owner_user_id == null ? null : Number(acct.owner_user_id)
      const viewerId = currentUserId == null ? null : Number(currentUserId)
      const canManagePrivacy =
        canSyncOne &&
        (acct.can_manage_privacy === true ||
          (ownerId != null && viewerId != null && ownerId === viewerId))
      return buildMailboxMenuItems({
        acct,
        canSyncOne,
        isHidden: ctx.isHidden,
        hasVisibilityKey: ctx.key != null,
        isPrivate,
        canManagePrivacy,
        onReconnect,
        onSync: canSyncOne
          ? () => void syncOne(ctx.realAccountId as number)
          : undefined,
        onToggleHide: ctx.key != null ? () => toggleMailbox(ctx.key as string) : undefined,
        onTogglePrivate:
          canManagePrivacy && onSetPrivacy
            ? () => void onSetPrivacy(ctx.realAccountId as number, !isPrivate)
            : undefined,
        onCleanUpInbox:
          canSyncOne && onCleanUpInbox
            ? () =>
                onCleanUpInbox(
                  ctx.realAccountId as number,
                  acct.email_address || acct.display_name || `Mailbox ${ctx.realAccountId}`,
                )
            : undefined,
        onOpenSettings,
      })
    },
    [
      onReconnect,
      onOpenSettings,
      syncOne,
      toggleMailbox,
      currentUserId,
      onSetPrivacy,
      onCleanUpInbox,
    ],
  )

  const openMenu = useCallback((x: number, y: number, items: MailboxMenuItem[]) => {
    if (items.length === 0) return
    setMenu({ x, y, items })
  }, [])

  const closeMenu = useCallback(() => setMenu(null), [])

  return { syncingId, menu, syncOne, buildItems, openMenu, closeMenu }
}
